import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Switch, message, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { categoriaDespesaService } from '../services/api';

const CategoriasDespesaList = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCategorias();
  }, []);

  const fetchCategorias = async () => {
    setLoading(true);
    try {
      const response = await categoriaDespesaService.list();
      setCategorias(response.data);
    } catch (error) {
      message.error('Erro ao carregar categorias');
      console.error(error);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Deseja excluir esta categoria?',
      okText: 'Sim',
      cancelText: 'Não',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await categoriaDespesaService.delete(id);
          message.success('Categoria excluída com sucesso');
          fetchCategorias();
        } catch (error) {
          message.error('Erro ao excluir categoria');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      if (editingId) {
        await categoriaDespesaService.update(editingId, values);
        message.success('Categoria atualizada com sucesso');
      } else {
        await categoriaDespesaService.create(values);
        message.success('Categoria criada com sucesso');
      }
      setIsModalVisible(false);
      fetchCategorias();
    } catch (error) {
      message.error('Erro ao salvar categoria');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
    },
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 150,
      render: (tipo) => {
        const tipoMap = {
          'SERVICO': { text: 'Serviço', color: 'blue' },
          'MATERIAL': { text: 'Material', color: 'green' },
          'ALUGUEL': { text: 'Aluguel', color: 'orange' },
          'SALARIO': { text: 'Salário', color: 'purple' },
          'IMPOSTO': { text: 'Imposto', color: 'red' },
          'OUTROS': { text: 'Outros', color: 'default' },
        };
        const info = tipoMap[tipo] || { text: tipo, color: 'default' };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'ativo',
      key: 'ativo',
      width: 100,
      render: (ativo) => (
        <Tag color={ativo ? 'green' : 'red'}>
          {ativo ? 'Ativo' : 'Inativo'}
        </Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Nova Categoria
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={categorias}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingId ? 'Editar Categoria' : 'Nova Categoria'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ ativo: true }}
        >
          <Form.Item
            name="nome"
            label="Nome"
            rules={[{ required: true, message: 'Nome é obrigatório' }]}
          >
            <Input placeholder="Ex: Material Didático" />
          </Form.Item>

          <Form.Item name="descricao" label="Descrição">
            <Input.TextArea rows={3} placeholder="Descrição da categoria" />
          </Form.Item>

          <Form.Item name="tipo" label="Tipo">
            <Select placeholder="Selecione o tipo">
              <Select.Option value="SERVICO">Serviço</Select.Option>
              <Select.Option value="MATERIAL">Material</Select.Option>
              <Select.Option value="ALUGUEL">Aluguel</Select.Option>
              <Select.Option value="SALARIO">Salário</Select.Option>
              <Select.Option value="IMPOSTO">Imposto</Select.Option>
              <Select.Option value="OUTROS">Outros</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="ativo" label="Status" valuePropName="checked">
            <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoriasDespesaList;
